import { useEffect, useState } from 'react';
import axios from 'axios';

/**
 * PGSS-5724: the Inventory History Summary view's labels, and the Edit Product labels that use the same names, are
 * settings (category `inventory_history_wording`) an admin can change on Settings → Profiles. Each entry is the
 * setting's field name and today's wording, which is used when the row is missing or blank. The field names must
 * match the backend's InventoryWording; the sentences themselves are built by the backend.
 */
export const WORDING_CATEGORY = 'inventory_history_wording';

const FIELD_PREFIX = 'Inventory history ';

export const INVENTORY_WORDING = {
  columnTime: ['Column: time', 'Time'],
  columnWhatHappened: ['Column: what happened', 'What happened'],
  columnInStock: ['Column: In Stock Qty', 'In Stock Qty'],
  columnAllocated: ['Column: allocated', 'Allocated'],
  columnTotal: ['Column: total', 'Total'],
  order: ['Label: order', 'Order'],
  container: ['Label: container', 'Container'],
  by: ['Label: by', 'by'],
  from: ['Label: from date', 'From'],
  to: ['Label: to date', 'To'],
  showAutomatic: ['Label: show automatic corrections', 'Show automatic corrections'],
  empty: ['Label: nothing to show', 'No stock changes for these filters.'],
  recordedAs: ['Label: recorded as', 'Recorded as:'],
  viewSummary: ['Label: Summary view', 'Summary'],
  viewDetailed: ['Label: Detailed view', 'Detailed'],
  chipHELD: ['Chip: allocated', 'Allocated'],
  chipRELEASED: ['Chip: released', 'Released'],
  chipRECEIVED: ['Chip: received', 'Received'],
  chipCOUNT: ['Chip: count', 'Count'],
  chipIMPORT: ['Chip: import', 'Import'],
  chipEDIT: ['Chip: edit', 'Edit'],
  chipCANCELLED: ['Chip: cancelled', 'Cancelled'],
  chipDELETED: ['Chip: deleted', 'Deleted'],
  chipCORRECTION: ['Chip: automatic', 'Auto'],
  chipOTHER: ['Chip: other', 'Other'],
  warehouseCount: ['Edit Product: warehouse count', 'Warehouse count'],
  warehouseCountHint: [
    'Edit Product: warehouse count hint',
    'In Stock Qty will be {available} ({count} counted − {allocated} allocated to orders).',
  ],
  warehouseCountBelowHint: [
    'Edit Product: warehouse count below allocated hint',
    'Only {count} on the shelf but {allocated} allocated to orders: {released} will be taken back from the newest orders, which move to Back Order. In Stock Qty will be 0.',
  ],
  warehouseCountNegativeHint: ['Edit Product: warehouse count negative hint', 'Warehouse count cannot be negative.'],
};

/** The settings field for `key`, lowercased as GET /api/setting returns it. */
export const wordingField = (key) => `${FIELD_PREFIX}${INVENTORY_WORDING[key][0]}`.toLowerCase();

/** `template` with each `{name}` replaced by `values[name]`; a name with no value is left as typed. */
export function fillWording(template, values = {}) {
  return String(template).replace(/\{([^{}]*)\}/g, (match, name) =>
    values[name] !== undefined && values[name] !== null ? String(values[name]) : match
  );
}

/**
 * A lookup over the saved settings (GET /api/setting?category=… response: lowercased field → value).
 * `w(key, values)` is the saved text, or today's wording when there is none, with placeholders filled in.
 */
export function wordingFrom(saved) {
  return (key, values) => {
    const entry = INVENTORY_WORDING[key];
    if (!entry) return key;
    const stored = saved?.[wordingField(key)];
    const text = typeof stored === 'string' && stored.trim() !== '' ? stored : entry[1];
    return values ? fillWording(text, values) : text;
  };
}

export const defaultWording = wordingFrom(null);

// Shared by every component that asks within a short while, and read again after it, so a change saved in Settings
// shows on the next screen opened without reloading the browser.
const REUSE_MS = 30 * 1000;
let pending = null;
let loadedAt = 0;

export function loadInventoryWording() {
  if (!pending || Date.now() - loadedAt > REUSE_MS) {
    loadedAt = Date.now();
    pending = axios
      .get('/api/setting', { params: { category: WORDING_CATEGORY } })
      .then((res) => res?.data || {})
      .catch(() => {
        // The defaults still show; try again on the next page load.
        pending = null;
        return {};
      });
  }
  return pending;
}

/** For tests: forget the loaded wording. */
export function resetInventoryWording() {
  pending = null;
}

/** The wording lookup; today's wording until the settings arrive. */
export function useInventoryWording() {
  const [w, setW] = useState(() => defaultWording);
  useEffect(() => {
    let alive = true;
    loadInventoryWording().then((saved) => {
      if (alive) setW(() => wordingFrom(saved));
    });
    return () => {
      alive = false;
    };
  }, []);
  return w;
}
